import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { captureError } from '@/lib/sentry';
import { isChunkLoadError, reloadOnceForChunkError } from '@/utils/lazyPage';

type ErrorBoundaryProps = {
  children: ReactNode;
  /** Optional label — sichtbar in der Error-UI */
  label?: string;
  /** Fallback Render — custom Error-UI statt Standard */
  fallback?: (error: Error, reset: () => void) => ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
};

/**
 * React-ErrorBoundary für die Sizilianer-App.
 *
 * Fängt Render-Errors in Child-Components ab, statt die ganze App weiß
 * zu lassen. Zeigt im Development-Modus den Stack-Trace.
 *
 * In Produktion: dezente Fehler-Karte mit Reload-Button + Link zur Startseite.
 *
 * Wrap Admin- und Public-Routes je einmal damit nicht die ganze App stirbt
 * wenn eine Component crasht.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Veralteter Chunk nach Deploy: ein Reload heilt das immer — automatisch,
    // genau einmal (Guard). Die Fehlerkarte erscheint nur, wenn auch der
    // Reload nicht half (echter Ausfall). Kein Sentry-Report für den Normalfall.
    if (isChunkLoadError(error) && reloadOnceForChunkError()) return;

    console.error(
      `[ErrorBoundary${this.props.label ? ` · ${this.props.label}` : ''}]`,
      error,
      errorInfo,
    );
    // An Sentry weiterreichen mit ErrorBoundary-Context
    captureError(error, {
      component: this.props.label ?? 'ErrorBoundary',
      action: 'render',
      extra: {
        componentStack: errorInfo.componentStack,
      },
    });
    this.setState({ errorInfo });
  }

  reset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  override render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.reset);
      }

      const isDev = import.meta.env.DEV;
      const errorMessage = this.state.error.message ?? 'Unbekannter Fehler';
      const errorStack = this.state.error.stack ?? '';
      const componentStack = this.state.errorInfo?.componentStack ?? '';

      return (
        <div className="min-h-screen bg-paper flex items-center justify-center p-6">
          <div className="max-w-2xl w-full bg-paper-2 border border-it-red/30 rounded-md overflow-hidden">
            <header className="px-6 py-4 bg-it-red/5 border-b border-it-red/20 flex items-center gap-3">
              <AlertTriangle className="size-6 text-it-red shrink-0" />
              <div>
                <h2 className="font-serif text-lg text-ink-2 leading-tight">
                  Ups — etwas ist schiefgegangen
                </h2>
                {this.props.label && (
                  <p className="font-sans text-xs text-ink-mute uppercase tracking-[0.18em] mt-0.5">
                    {this.props.label}
                  </p>
                )}
              </div>
            </header>

            <div className="px-6 py-5">
              <p className="font-sans text-sm text-ink-soft mb-3">
                Diese Seite konnte nicht geladen werden. Versuch&apos;s mit
                Neuladen — wenn der Fehler bleibt, gib uns kurz Bescheid mit
                der unten stehenden Meldung.
              </p>

              <div className="font-mono text-xs text-it-red bg-paper border border-rule rounded p-3 mb-4 break-all">
                {errorMessage}
              </div>

              {isDev && (errorStack || componentStack) && (
                <details className="mb-4">
                  <summary className="cursor-pointer font-sans text-xs text-ink-mute uppercase tracking-[0.18em] mb-2">
                    Stack-Trace (nur Dev)
                  </summary>
                  {errorStack && (
                    <pre className="font-mono text-2xs text-ink-soft bg-paper border border-rule rounded p-3 overflow-x-auto whitespace-pre-wrap mb-2 max-h-64">
                      {errorStack}
                    </pre>
                  )}
                  {componentStack && (
                    <pre className="font-mono text-2xs text-ink-soft bg-paper border border-rule rounded p-3 overflow-x-auto whitespace-pre-wrap max-h-64">
                      {componentStack}
                    </pre>
                  )}
                </details>
              )}

              <div className="flex flex-wrap gap-3 mt-5">
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="inline-flex items-center gap-2 bg-gold-2 text-paper px-4 py-2 rounded font-sans text-xs uppercase tracking-[0.18em] font-semibold hover:bg-ink-2 transition-colors"
                >
                  <RefreshCw className="size-4" />
                  Seite neu laden
                </button>
                <button
                  type="button"
                  onClick={this.reset}
                  className="inline-flex items-center gap-2 bg-paper border border-rule text-ink-2 px-4 py-2 rounded font-sans text-xs uppercase tracking-[0.18em] font-semibold hover:bg-paper-3 transition-colors"
                >
                  Erneut versuchen
                </button>
                <a
                  href="/"
                  className="inline-flex items-center gap-2 px-4 py-2 font-sans text-xs uppercase tracking-[0.18em] font-semibold text-ink-soft hover:text-gold-2 transition-colors"
                >
                  Zur Startseite
                </a>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
